<template>
  <div class="col-sm-12">
    <div class="card">

      <div v-if="loading" class="loading-spinner">
        <i class="fa fa-spinner fa-spin"></i> Loading...
      </div>

      <template v-else-if="trustDetails">
        <div class="summary-block">
          <div class="summary-item">
            <span class="label">Dealer</span>
            <span class="value">{{ trustDetails.header.dealer_name || "N/A" }}</span>
          </div>
          <div class="summary-item">
            <span class="label">Date</span>
            <span class="value">{{ formatDate(trustDetails.header.created_at) }}</span>
          </div>
          <div class="summary-item">
            <span class="label">Remarks</span>
            <span class="value">{{ trustDetails.header.remarks || "-" }}</span>
          </div>
          <div class="summary-item">
            <span class="label">Created By</span>
            <span class="value">{{ trustDetails.header.created_by_name || "N/A" }}</span>
          </div>
        </div>

        <!-- Two-column layout: Items (left) / Documents (right) -->
        <div class="split-sections">
          <div class="split-section">
            <h5 class="section-title">Items</h5>
            <div class="table-responsive" v-if="trustDetails.items.length > 0">
              <table class="table">
                <thead>
                <tr class="border-bottom-primary">
                  <th v-for="key in itemColumns" :key="key">{{ formatHeader(key) }}</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(item, idx) in trustDetails.items" :key="idx">
                  <td v-for="key in itemColumns" :key="key">{{ item[key] }}</td>
                </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="no-data-message">No items found.</div>
          </div>

          <div class="split-section">
            <h5 class="section-title">Documents</h5>
            <div class="table-responsive" v-if="trustDetails.documents.length > 0">
                <table class="table">
                <thead>
                <tr class="border-bottom-primary">
                    <th scope="col">Document Name</th>
                    <th scope="col">Actions</th>
                </tr>
                </thead>
                <tbody>
                <template v-for="(doc, idx) in trustDetails.documents" :key="idx">
                    <tr>
                    <td>{{ doc.document_name || "-" }}</td>
                    <td>
                        <div class="action-icons">
                        <a
                            v-if="doc.document_file_url"
                            :href="doc.document_file_url"
                            target="_blank"
                            rel="noopener"
                            class="document-btn"
                            :title="doc.document_file_name"
                        >
                            <i class="fa fa-external-link-alt"></i>
                            <span>View</span>
                        </a>
                        <span v-else class="no-document">&mdash;</span>

                        <button
                            v-if="doc.is_multiple == 1"
                            type="button"
                            class="reupload-btn"
                            @click="openReuploadModal(idx)"
                        >
                            <i class="fa fa-upload"></i>
                            <span>Reupload</span>
                        </button>
                        </div>
                    </td>
                    </tr>

                    <!-- Reuploaded versions for this document -->
                    <tr v-if="doc.reuploads && doc.reuploads.length > 0" class="reupload-row">
                    <td colspan="2">
                        <div class="reupload-list">
                        <div class="reupload-list-title">Reuploaded Versions</div>
                            <div
                                v-for="(reupload, rIdx) in doc.reuploads"
                                :key="rIdx"
                                class="reupload-list-item"
                            >
                                <span class="reupload-date">{{ formatDate(reupload.reupload_date) }}</span>
                                <span class="reupload-filename">{{ reupload.document_file_name || "-" }}</span>
                                <a
                                    v-if="reupload.document_file_url"
                                    :href="reupload.document_file_url"
                                    target="_blank"
                                    rel="noopener"
                                    class="document-btn small"
                                    :title="reupload.document_file_name"
                                >
                                <i class="fa fa-external-link-alt"></i>
                                <span>View</span>
                                </a>
                            </div>
                        </div>
                    </td>
                    </tr>
                </template>
                </tbody>
                </table>
            </div>
            <div v-else class="no-data-message">No documents found.</div>
            </div>
        </div>
      </template>

      <div v-else class="no-data-message">
        Trust record not found.
      </div>
    </div>

    <!-- Reupload Modal (inline) -->
    <div v-if="showReuploadModal" class="reupload-modal-overlay" @click.self="closeReuploadModal">
      <div class="reupload-modal">
        <div class="reupload-modal-header">
          <h5>Reupload Document</h5>
          <button type="button" class="close-btn" @click="closeReuploadModal">
            <i class="fa fa-times"></i>
          </button>
        </div>

        <div class="reupload-modal-body">
          <p class="document-label">{{ activeDoc?.document_file_name || "" }}</p>

          <div
            class="document-image-container upload-box"
            :class="{ 'drag-active': isDragging }"
            @mouseenter="showFileActions = true"
            @mouseleave="showFileActions = false"
            @click="triggerFileInput"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="handleDrop"
          >
            <!-- Image preview -->
            <img
              v-if="filePreview && isImageDocument(fileType)"
              :src="filePreview"
              alt="Document File"
              class="document-image"
            />

            <!-- PDF -->
            <div v-else-if="filePreview && isPdfDocument(fileType)" class="pdf-preview">
              <i class="fas fa-file-pdf"></i>
              <span>{{ fileName || "Document" }}</span>
              <small>PDF Document</small>
            </div>

            <!-- Word -->
            <div v-else-if="filePreview && isWordDocument(fileType)" class="pdf-preview">
              <i class="fas fa-file-word"></i>
              <span>{{ fileName || "Document" }}</span>
              <small>Word Document</small>
            </div>

            <!-- Excel -->
            <div v-else-if="filePreview && isExcelDocument(fileType)" class="pdf-preview">
              <i class="fas fa-file-excel"></i>
              <span>{{ fileName || "Document" }}</span>
              <small>Excel Document</small>
            </div>

            <!-- Invalid -->
            <div v-else-if="filePreview" class="invalid-preview">
              <i class="fas fa-exclamation-triangle"></i>
              <span>Invalid Document</span>
              <small>Unsupported format</small>
            </div>

            <!-- Placeholder -->
            <div v-else class="document-placeholder">
              <i :class="isDragging ? 'fas fa-cloud-download-alt' : 'fas fa-file-upload'"></i>
              <span>{{ isDragging ? "Drop file to upload" : "Click or Drag & Drop to Upload" }}</span>
              <small>Supported: PDF, JPG, PNG, JPEG, DOCX, XLSX</small>
            </div>

            <!-- Actions overlay -->
            <div
              class="document-actions-overlay"
              v-if="showFileActions && filePreview && isValidDocument(filePreview, fileType)"
            >
              <div class="action-buttons">
                <button class="action-btn change-btn" @click.stop="triggerFileInput" title="Change">
                  <i class="fas fa-sync"></i>
                </button>
                <button class="action-btn remove-btn" @click.stop="removeSelectedFile" title="Remove">
                  <i class="fas fa-trash"></i>
                </button>
              </div>
              <div class="action-labels">
                <span>Change</span>
                <span>Remove</span>
              </div>
            </div>

            <!-- Upload progress -->
            <div v-if="fileUploading" class="upload-progress">
              <div class="progress-bar">
                <div class="progress-fill" :style="{ width: fileProgress + '%' }"></div>
              </div>
              <span>{{ fileProgress }}%</span>
            </div>
          </div>

          <input
            type="file"
            ref="reuploadFileInput"
            @change="handleFileChange"
            accept=".pdf,image/jpeg,image/jpg,image/png,.docx,.xlsx"
            class="file-input-hidden"
          />
        </div>

        <div class="reupload-modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeReuploadModal">Cancel</button>
          <button
            type="button"
            class="btn btn-success"
            :disabled="!selectedFile || reuploadSaving"
            @click="handleReuploadSubmit"
          >
            <span v-if="reuploadSaving" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            <span v-if="!reuploadSaving">Upload</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchTrustDetailsById, reuploadTrustDocument } from "@/services/dataService";
import { useToast } from "vue-toastification";

// Keys we never want rendered as columns, even if the backend includes them
const EXCLUDED_ITEM_KEYS = ["id", "trust_header_id", "item_id"];

export default {
  name: "TrustDetailsView",
  data() {
    return {
      trustId: this.$route.params.id,
      token: localStorage.getItem("token_set") || "",
      loading: false,
      trustDetails: null,

      // Modal state
      showReuploadModal: false,
      reuploadSaving: false,
      activeDocIndex: null,

      // File upload state (for the modal's upload box)
      selectedFile: null,
      fileName: "",
      fileType: null,
      filePreview: null,
      isDragging: false,
      showFileActions: false,
      fileUploading: false,
      fileProgress: 0,
    };
  },
  computed: {
    itemColumns() {
      if (!this.trustDetails?.items?.length) return [];
      return Object.keys(this.trustDetails.items[0]).filter(
        (key) => !EXCLUDED_ITEM_KEYS.includes(key)
      );
    },
    activeDoc() {
      return this.activeDocIndex !== null
        ? this.trustDetails.documents[this.activeDocIndex]
        : null;
    },
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  methods: {
    async getTrustDetails() {
      try {
        this.loading = true;
        const response = await fetchTrustDetailsById(this.trustId, this.token);
        this.trustDetails = response.data?.data || null;
      } catch (error) {
        console.error("Error fetching trust details:", error);
        this.trustDetails = null;
      } finally {
        this.loading = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    formatHeader(key) {
      return key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },

    // ---- Modal open/close ----
    openReuploadModal(idx) {
      this.activeDocIndex = idx;
      this.resetFileState();
      this.showReuploadModal = true;
    },

    closeReuploadModal() {
      this.showReuploadModal = false;
      this.activeDocIndex = null;
      this.resetFileState();
    },

    resetFileState() {
      this.selectedFile = null;
      this.fileName = "";
      this.fileType = null;
      this.filePreview = null;
      this.isDragging = false;
      this.showFileActions = false;
      this.fileUploading = false;
      this.fileProgress = 0;
      if (this.$refs.reuploadFileInput) this.$refs.reuploadFileInput.value = "";
    },

    // ---- File input handling (mirrors AddDocumentTemplate.vue) ----
    triggerFileInput() {
      this.$refs.reuploadFileInput.click();
    },

    handleFileChange(event) {
      const file = event.target.files[0];
      if (file) this.processSelectedFile(file);
    },

    handleDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer.files && event.dataTransfer.files[0];
      if (file) this.processSelectedFile(file);
    },

    async processSelectedFile(file) {
      if (!this.validateFile(file)) return;

      this.selectedFile = file;
      this.fileName = file.name;
      this.fileType = file.type;
      this.fileUploading = true;
      this.fileProgress = 0;

      await this.simulateUploadProgress();

      const reader = new FileReader();
      reader.onload = (e) => {
        this.filePreview = e.target.result;
        this.fileUploading = false;
      };
      reader.readAsDataURL(file);
    },

    simulateUploadProgress() {
      return new Promise((resolve) => {
        const interval = setInterval(() => {
          this.fileProgress += 10;
          if (this.fileProgress >= 100) {
            clearInterval(interval);
            resolve();
          }
        }, 100);
      });
    },

    removeSelectedFile() {
      this.selectedFile = null;
      this.fileName = "";
      this.fileType = null;
      this.filePreview = null;
      if (this.$refs.reuploadFileInput) this.$refs.reuploadFileInput.value = "";
    },

    isImageDocument(type) {
      return type && type.startsWith("image/");
    },
    isPdfDocument(type) {
      return type === "application/pdf";
    },
    isWordDocument(type) {
      return type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    },
    isExcelDocument(type) {
      return type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    },
    isValidDocument(url, type) {
      if (!url || !type) return false;
      return (
        this.isImageDocument(type) ||
        this.isPdfDocument(type) ||
        this.isWordDocument(type) ||
        this.isExcelDocument(type)
      );
    },

    validateFile(file) {
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        this.toast.error("File size must be less than 5MB");
        return false;
      }
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];
      if (!allowedTypes.includes(file.type)) {
        this.toast.error("Please select a PDF, JPG, PNG, JPEG, DOCX, or XLSX file");
        return false;
      }
      return true;
    },

    // ---- Submit ----
    async handleReuploadSubmit() {
      if (!this.selectedFile || !this.activeDoc) return;

      try {
        this.reuploadSaving = true;

        const base64File = await this.toBase64(this.selectedFile);

        const payload = {
          trust_header_id: this.trustId,
          document_template_id: this.activeDoc.document_template_id,
          document_file: base64File,
          document_file_name: this.fileName,
        };

        const response = await reuploadTrustDocument(payload, this.token);

        if (response.data.status === "success") {
          this.toast.success(response.data.message || "Document reuploaded successfully.");
          this.closeReuploadModal();
          await this.getTrustDetails();
        } else {
          this.toast.error(response.data.message || "Failed to reupload document.");
        }
      } catch (error) {
        console.error("Error reuploading document:", error);
        this.toast.error(
          error.response?.data?.message || "Failed to reupload document."
        );
      } finally {
        this.reuploadSaving = false;
      }
    },

    toBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.onerror = (error) => reject(error);
      });
    },

    goBack() {
      this.$router.back();
    },
  },
  mounted() {
    this.getTrustDetails();
  },
};
</script>

<style scoped>
.card {
  padding: 10px;
}

.loading-spinner {
  font-size: 18px;
  text-align: center;
  margin-top: 20px;
  color: #007bff;
}

.summary-block {
  display: flex;
  gap: 30px;
  flex-wrap: wrap;
  padding: 10px;
  margin-bottom: 15px;
  background-color: #f8f9fa;
  border-radius: 6px;
}

.summary-item {
  display: flex;
  flex-direction: column;
}

.summary-item .label {
  font-size: 12px;
  color: #6c757d;
  text-transform: uppercase;
}

.summary-item .value {
  font-size: 14px;
  color: #333;
  font-weight: 600;
}

/* ---- Split sections (Items left / Documents right) ---- */
.split-sections {
  display: flex;
  gap: 20px;
  align-items: flex-start;
  padding: 0 10px;
}

.split-section {
  flex: 1;
  min-width: 0;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 15px;
}

.section-title {
  margin: 0 0 12px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e5e7eb;
  color: #333;
}

.action-icons {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.document-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 20px;
  background-color: #eaf2ff;
  color: #1c66ad;
  text-decoration: none;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  border: 1px solid #cfe1fb;
  transition: background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.document-btn:hover {
  background-color: #1c66ad;
  color: #fff;
  border-color: #1c66ad;
  box-shadow: 0 3px 8px rgba(28, 102, 173, 0.3);
  transform: translateY(-1px);
}

.reupload-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 20px;
  background-color: #fff7e6;
  color: #b45309;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  border: 1px solid #fde3a7;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.reupload-btn:hover {
  background-color: #b45309;
  color: #fff;
  border-color: #b45309;
  box-shadow: 0 3px 8px rgba(180, 83, 9, 0.3);
  transform: translateY(-1px);
}

.no-document {
  color: #9ca3af;
  font-size: 14px;
}

.no-data-message {
  text-align: center;
  padding: 20px;
  color: #6c757d;
  font-style: italic;
}

/* ---- Reupload Modal ---- */
.reupload-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.reupload-modal {
  background: #fff;
  width: 420px;
  max-width: 90%;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 10px 35px rgba(0, 0, 0, 0.2);
}

.reupload-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.reupload-modal-header h5 {
  margin: 0;
  color: #333;
}

.close-btn {
  border: none;
  background: transparent;
  font-size: 16px;
  color: #6b7280;
  cursor: pointer;
}

.document-label {
  font-weight: 600;
  color: #374151;
  margin-bottom: 10px;
  font-size: 14px;
}

.upload-box {
  height: 200px;
}

.file-input-hidden {
  display: none;
}

.document-image-container {
  position: relative;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.document-image-container:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.document-image-container.drag-active {
  box-shadow: 0 0 0 3px #1c66ad, 0 4px 12px rgba(28, 102, 173, 0.3);
  transform: scale(1.02);
}

.document-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.document-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #f3f4f6;
  color: #6b7280;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 0.75rem;
  text-align: center;
}

.document-placeholder i {
  font-size: 1.6rem;
  margin-bottom: 0.4rem;
  color: #9ca3af;
}

.document-placeholder span {
  font-size: 0.8rem;
  font-weight: 500;
}

.document-placeholder small {
  display: block;
  margin-top: 4px;
  font-size: 0.7rem;
  color: #9ca3af;
}

.pdf-preview,
.invalid-preview {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #fef3f2;
  color: #dc2626;
  border-radius: 8px;
  padding: 0.75rem;
  text-align: center;
}

.pdf-preview i,
.invalid-preview i {
  font-size: 2.2rem;
  margin-bottom: 0.4rem;
}

.pdf-preview span {
  font-weight: 600;
  color: #374151;
  font-size: 0.8rem;
  word-break: break-word;
}

.pdf-preview small,
.invalid-preview small {
  display: block;
  margin-top: 4px;
  font-size: 0.7rem;
  color: #6b7280;
}

.document-actions-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.action-btn {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  cursor: pointer;
  color: white;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

.change-btn {
  background: linear-gradient(45deg, #4caf50, #66bb6a);
}

.remove-btn {
  background: linear-gradient(45deg, #f44336, #e53935);
}

.action-buttons {
  display: flex;
  gap: 10px;
  margin-bottom: 8px;
}

.action-labels {
  display: flex;
  gap: 16px;
  color: white;
  font-size: 10px;
  font-weight: 600;
}

.upload-progress {
  position: absolute;
  bottom: 8px;
  left: 8px;
  right: 8px;
  background: rgba(255, 255, 255, 0.9);
  padding: 6px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.progress-bar {
  flex: 1;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(45deg, #4caf50, #66bb6a);
  transition: width 0.3s ease;
}

.upload-progress span {
  font-size: 0.7rem;
  font-weight: 600;
  color: #374151;
}

.reupload-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #e5e7eb;
}

.btn {
  padding: 8px 18px;
  border: none;
  border-radius: 5px;
  font-size: 14px;
  cursor: pointer;
  min-width: 90px;
}

.btn-secondary {
  background-color: #6b7280;
  color: white;
}

.btn-success {
  background-color: #28a745;
  color: white;
}

.btn-success:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
}

.reupload-row td {
  padding: 0;
  border-top: none;
}

.reupload-list {
  background-color: #fafbfc;
  border-left: 3px solid #fde3a7;
  padding: 10px 14px;
  margin: 0 0 8px;
}

.reupload-list-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #b45309;
  margin-bottom: 6px;
  letter-spacing: 0.5px;
}

.reupload-list-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 0;
  font-size: 13px;
  color: #374151;
}

.reupload-date {
  color: #6c757d;
  min-width: 90px;
}

.reupload-filename {
  flex: 1;
}

.document-btn.small {
  padding: 4px 10px;
  font-size: 12px;
}

/* Stack columns on smaller screens */
@media (max-width: 900px) {
  .split-sections {
    flex-direction: column;
  }
}
</style>